import React from "react";
import { Avatar, AvatarFallback } from "../../atoms/Avatar/index.js";
import { initialsOf } from "../../lib/initials.js";

/**
 * What FilterOptionList renders for an option: its plain label, or — for a
 * person option — the person label and the ids that name the control by the
 * name alone and describe it by the email.
 */
export function personOption(
  value: string,
  label: React.ReactNode,
  person:
    | { readonly email?: string | null | undefined; readonly idBase: string }
    | undefined,
): {
  readonly label: React.ReactNode;
  readonly aria: {
    readonly "aria-labelledby"?: string;
    readonly "aria-describedby"?: string | undefined;
  };
} {
  if (!person) return { label, aria: {} };
  const nameId = `${person.idBase}-name`;
  const emailId = `${person.idBase}-email`;
  return {
    label: (
      <FilterOptionPerson
        id={value}
        name={label}
        email={person.email}
        nameId={nameId}
        emailId={emailId}
      />
    ),
    aria: {
      "aria-labelledby": nameId,
      "aria-describedby": person.email ? emailId : undefined,
    },
  };
}

/**
 * A person option's label, as PeopleField's rows read: an avatar seeded by
 * the person's id, the name, and the email under it. The control is named by
 * `nameId` and described by `emailId`, so the address is not read as part of
 * the name. Internal to FilterOptionList.
 */
export function FilterOptionPerson({
  id,
  name,
  email,
  nameId,
  emailId,
}: {
  /** The option's value: the person's id. */
  readonly id: string;
  readonly name: React.ReactNode;
  readonly email: string | null | undefined;
  readonly nameId: string;
  readonly emailId: string;
}): React.ReactElement {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <Avatar size="xs" seed={id} aria-hidden="true">
        <AvatarFallback>
          {initialsOf(typeof name === "string" ? name : id)}
        </AvatarFallback>
      </Avatar>
      <span className="flex min-w-0 flex-col">
        <span id={nameId} className="truncate">
          {name}
        </span>
        {email ? (
          <span id={emailId} className="truncate text-xs text-pb-text-muted">
            {email}
          </span>
        ) : null}
      </span>
    </span>
  );
}
